import * as LucideIcons from "lucide-react";
import { useSiteContent } from "@/hooks/useSiteContent";
import { useAudienceCards } from "@/hooks/useAudienceCards";

export const Audience = () => {
  const { content } = useSiteContent('audience');
  const { cards, isLoading } = useAudienceCards();

  // Fallback to hardcoded data if no dynamic cards
  const displayCards = cards.length > 0 ? cards : [
    {
      id: '1',
      icon: 'Zap',
      title: "Stress & Spanning",
      description: "Laat los en vind innerlijke rust na een drukke dag. Voor iedereen die diep wil ademen en tot zichzelf wil komen.",
      color: "from-gold to-gold-light",
      displayOrder: 1,
      isActive: true
    },
    {
      id: '2',
      icon: 'Heart',
      title: "Hormonale balans",
      description: "Ondersteunt de natuurlijke flow van je cyclus en brengt harmonie. Voor vrouwen die balans zoeken in elke levensfase.",
      color: "from-primary to-primary-glow",
      displayOrder: 2,
      isActive: true
    },
    {
      id: '3',
      icon: 'Moon',
      title: "Slaap & Ontspanning",
      description: "Een zacht avondritueel dat je begeleidt naar diepe, helende slaap. Voor rustzoekende zielen.",
      color: "from-emerald to-emerald-light",
      displayOrder: 3,
      isActive: true
    },
  ];

  if (isLoading) {
    return (
      <section className="py-24 bg-gradient-to-b from-muted/20 to-background">
        <div className="container mx-auto px-4 text-center">
          <div className="animate-pulse">Content laden...</div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative py-24 bg-gradient-to-b from-muted/20 to-background overflow-hidden">
      {/* Flowing Energy Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="absolute w-96 h-96 rounded-full bg-gradient-to-r from-gold to-primary opacity-20 blur-3xl"
              style={{
                left: `${20 + i * 30}%`,
                top: `${10 + i * 20}%`,
                animation: `float 8s ease-in-out infinite ${i * 2}s`,
              }}
            />
          ))}
        </div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-primary mb-6 animate-on-scroll">
            {content?.title || 'Voor wie is Lunssence?'}
          </h2>
          
          <div className="w-16 h-1 bg-gradient-gold mx-auto mb-8 animate-on-scroll [animation-delay:0.2s]" />
          
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto animate-on-scroll [animation-delay:0.4s]">
            {content?.description || 'Onze thee ondersteunt je in de fases van het leven waarin je lichaam en geest extra zorg nodig hebben'}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {displayCards.map((card, index) => {
            const IconComponent = (LucideIcons as any)[card.icon] || LucideIcons.Heart;
            return (
              <div
                key={card.id}
                className="group perspective-1000 animate-on-scroll"
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <div className="card-3d bg-card rounded-2xl p-8 h-full shadow-lg hover:shadow-gold transition-all duration-500 transform-gpu">
                  {/* Icon with gradient background */}
                  <div className={`w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-r ${card.color || 'from-gold to-gold-light'} flex items-center justify-center shadow-lg`}>
                    <IconComponent className="w-8 h-8 text-gold drop-shadow-[0_0_3px_rgba(255,255,255,0.9)]" />
                  </div>
                  
                  {/* Content */}
                  <h3 className="font-serif text-2xl font-bold text-primary mb-4 text-center">
                    {card.title}
                  </h3>
                  
                  <p className="text-muted-foreground text-center leading-relaxed">
                    {card.description}
                  </p>
                  
                  {/* Hover glow effect */}
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-gold/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                </div>
              </div>
            );
          })}
        </div>

        {/* Additional CTA */}
        <div className="text-center mt-16 animate-on-scroll [animation-delay:0.8s]">
          <p className="text-lg text-muted-foreground mb-4">
            Herken jij jezelf in één van deze situaties?
          </p>
          <div className="inline-block px-8 py-3 rounded-full bg-gradient-gold text-primary font-semibold shadow-gold">
            Dan is Lunssence perfect voor jou ✨
          </div>
        </div>
      </div>
    </section>
  );
};